/* ──────────────────────────────────────────────────────────────────────────
   TwinMsPage — /twinms/
   ──────────────────────────────────────────────────────────────────────────
   A long-scroll product page for Design Zone's digital-twin platform.
   Reads from window.DZ_TWINMS (content/twinms.js). Renders 8 stacked
   sections (hero → 3D → monitors → performance → alerts → pilot → bim →
   next) followed by the page-level CtaStrip + Footer (declared in the
   HTML shell, not here).

   Architecture choices:
     • Aliased React hooks — same convention as SolutionsHub so two pages
       loaded in the same Babel transform never collide on `useState`.
     • useTmsReveal — IntersectionObserver factory shared by every section
       that animates in on first reveal (the same pattern used on the
       Solutions outcome arc and the Projects detail page).
     • TmsCountUp — easing animator for the hero stat strip; starts when
       the strip first scrolls into view, never restarts on subsequent
       intersections (startedRef guard).
     • All visuals scoped to `.dz-tms-*` class names so we can ship CSS
       in home.css without leaking into Solutions or Projects styles.
   ──────────────────────────────────────────────────────────────────────────*/

const { useState: useTmsState, useEffect: useTmsEffect, useRef: useTmsRef } = React;

/* ── useTmsReveal — adds `.is-in-view` to the ref'd element the first
   time it enters the viewport. */
// Path-based AR localization for hardcoded UI labels (content from content/twinms_ar.js).
function dzIsAr() { return typeof window !== 'undefined' && window.location.pathname.startsWith('/ar/'); }
function dzT(en, ar) { return dzIsAr() ? ar : en; }

function useTmsReveal(threshold) {
  const ref = useTmsRef(null);
  useTmsEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) el.classList.add('is-in-view'); },
      { threshold: threshold || 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

/* ── TmsCountUp — animates an integer from 0 → `to` over `duration` ms,
   easing with the standard easeOutCubic. Runs exactly once, the first
   time the parent strip becomes visible. */
function TmsCountUp({ to, prefix, suffix, duration }) {
  const [value, setValue] = useTmsState(0);
  const ref = useTmsRef(null);
  const startedRef = useTmsRef(false);

  useTmsEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting || startedRef.current) return;
      startedRef.current = true;
      const dur = duration || 1400;
      const t0 = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        setValue(Math.round(eased * to));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [to, duration]);

  return (
    <span ref={ref} className="dz-tms-count">
      {prefix || ''}{value.toLocaleString('en-US')}{suffix || ''}
    </span>
  );
}

/* ── TmsLiveGauges — HUD overlay rendered on top of the hero video.
   Three radial gauges on the left (Structural / Energy / HVAC) and two
   bar groups on the right (Occupancy by floor / Maintenance alerts).
   Values drift on a setInterval so the read feels like live telemetry
   coming off the building, not canned footage. The widget has
   `aria-hidden` and `pointer-events: none` so it's a pure visual layer.
   `videoRef` is the parent's <video> ref — we listen to its currentTime
   so the HUD only appears during the digital-twin scene of the loop
   (t≈2s onward) and disappears as the loop returns to the building. */
function TmsLiveGauges({ videoRef }) {
  const [v, setV] = useTmsState({
    structural: 98,
    energy:     45,
    hvac:       82,
    occ:        [62, 78, 54, 88, 71, 46, 83],
    alerts:     [12, 24, 9, 31, 17]
  });
  /* Visibility tracks the video's playhead. The video is 5s, the digital
     twin scene begins at ~2s. Fade in at 2s, out as the loop wraps. */
  const [visible, setVisible] = useTmsState(false);
  useTmsEffect(() => {
    const vid = videoRef && videoRef.current;
    if (!vid) return;
    const onTime = () => {
      const t = vid.currentTime;
      setVisible(t >= 2);
    };
    vid.addEventListener('timeupdate', onTime);
    /* Hide on loop (when currentTime resets to 0) */
    vid.addEventListener('seeked', onTime);
    onTime();
    return () => {
      vid.removeEventListener('timeupdate', onTime);
      vid.removeEventListener('seeked', onTime);
    };
  }, [videoRef]);

  useTmsEffect(() => {
    /* Continuous drift — fires every 200ms with tiny per-tick amplitude
       so the motion never pauses or jumps. Combined with the CSS
       transition this reads as smooth, always-alive telemetry rather
       than discrete steps every second. Values stay inside operationally
       plausible ranges (structural high+tight, energy/HVAC freer, bars
       swing the most). */
    const drift = (val, min, max, amp) => {
      const next = val + (Math.random() - 0.5) * amp;
      return Math.max(min, Math.min(max, next));
    };
    const id = setInterval(() => {
      setV(prev => ({
        structural: drift(prev.structural, 94, 100, 0.20),
        energy:     drift(prev.energy,     38, 56,  0.45),
        hvac:       drift(prev.hvac,       75, 89,  0.32),
        occ:        prev.occ.map(x    => drift(x, 22, 95, 1.6)),
        alerts:     prev.alerts.map(x => drift(x, 5,  35, 1.3))
      }));
    }, 200);
    return () => clearInterval(id);
  }, []);

  /* Round once for display so the DOM doesn't churn 5 decimal places. */
  const r = (n) => Math.round(n);

  /* No wrapper columns — each widget is absolutely positioned by `pos`
     class so it lands exactly on top of the corresponding baked-in
     widget in the video. Coordinates measured from the source footage
     (see CSS). */
  return (
    <div className={`dz-tms-hud ${visible ? 'is-visible' : ''}`} aria-hidden="true">
      <TmsGauge pos="g1" label="Structural integrity" value={r(v.structural)} max={100} />
      <TmsGauge pos="g2" label="Energy consumption"  value={r(v.energy)}      max={100} />
      <TmsGauge pos="g3" label="HVAC efficiency"      value={r(v.hvac)}        max={100} />
      <TmsBars  pos="b1" label="Occupancy by floor"   values={v.occ}    max={100} />
      <TmsBars  pos="b2" label="Maintenance alerts"   values={v.alerts} max={40}  />
    </div>
  );
}

/* Single radial gauge — SVG ring + centred percentage. The ring is
   rendered with stroke-dasharray so the filled arc is just a function
   of the value. CSS handles the cyan glow and absolute positioning
   via `pos` (g1/g2/g3) — each pos lands the widget on the matching
   baked-in gauge in the video footage. */
function TmsGauge({ pos, label, value, max }) {
  const size = 60;
  const stroke = 5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / max) * c;
  return (
    <div className={`dz-tms-hud__gauge dz-tms-hud__gauge--${pos}`}>
      <div className="dz-tms-hud__gauge-label">{label}: {value}%</div>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="dz-tms-hud__gauge-svg" preserveAspectRatio="xMidYMid meet">
        <circle cx={size/2} cy={size/2} r={r} className="dz-tms-hud__gauge-track" strokeWidth={stroke} fill="none" />
        <circle
          cx={size/2} cy={size/2} r={r}
          className="dz-tms-hud__gauge-fill"
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`}
        />
        <text
          x="50%" y="50%"
          dominantBaseline="central"
          textAnchor="middle"
          className="dz-tms-hud__gauge-num"
        >{value}%</text>
      </svg>
    </div>
  );
}

/* Bar group — N animated rects whose heights track values[]. The `pos`
   prop (b1/b2) places the widget over the matching baked-in chart
   in the video. Each rect's y/height transitions for the drift. */
function TmsBars({ pos, label, values, max }) {
  const w = 110, h = 36, gap = 3;
  const barW = (w - (values.length - 1) * gap) / values.length;
  return (
    <div className={`dz-tms-hud__bars dz-tms-hud__bars--${pos}`}>
      <div className="dz-tms-hud__bars-label">{label}</div>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="dz-tms-hud__bars-svg" preserveAspectRatio="xMidYMid meet">
        {values.map((val, i) => {
          const bh = Math.max(2, (val / max) * h);
          const x = i * (barW + gap);
          const y = h - bh;
          return (
            <rect
              key={i}
              x={x} y={y}
              width={barW}
              height={bh}
              className="dz-tms-hud__bar"
            />
          );
        })}
      </svg>
    </div>
  );
}

/* ── Inline SVG icon set for the monitors grid. Keeping these inline (no
   external sprite) so the section ships in one document — no extra
   roundtrips, and the stroke colour can inherit from the tile theme. */
function TmsIcon({ name }) {
  const common = { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': 'true' };
  switch (name) {
    case 'air':
      return <svg {...common}><path d="M3 8h11a3 3 0 1 0-3-3"/><path d="M3 12h17a3 3 0 1 1-3 3"/><path d="M3 16h9"/></svg>;
    case 'energy':
      return <svg {...common}><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"/></svg>;
    case 'water':
      return <svg {...common}><path d="M12 3s6 6 6 11a6 6 0 1 1-12 0c0-5 6-11 6-11z"/></svg>;
    case 'occupancy':
      return <svg {...common}><circle cx="9" cy="8" r="3"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><circle cx="17" cy="6" r="2.4"/><path d="M14 13c1-.7 2-1 3-1 2.6 0 4 2 4 4"/></svg>;
    case 'hvac':
      return <svg {...common}><circle cx="12" cy="12" r="2"/><path d="M12 4v4M12 16v4M4 12h4M16 12h4M6 6l2.8 2.8M15.2 15.2 18 18M6 18l2.8-2.8M15.2 8.8 18 6"/></svg>;
    case 'door':
      return <svg {...common}><path d="M6 3h9a2 2 0 0 1 2 2v16H6z"/><path d="M14 12h.01"/><path d="M3 21h18"/></svg>;
    case 'light':
      return <svg {...common}><path d="M9 18h6"/><path d="M10 21h4"/><path d="M12 3a6 6 0 0 0-4 10.5c1 .9 1.5 2 1.5 3.5h5c0-1.5.5-2.6 1.5-3.5A6 6 0 0 0 12 3z"/></svg>;
    case 'parking':
      return <svg {...common}><rect x="4" y="4" width="16" height="16" rx="2.5"/><path d="M10 16V8h3.2a2.4 2.4 0 0 1 0 4.8H10"/></svg>;
    default:
      return null;
  }
}

/* ════════════════════════════════════════════════════════════════════════
   HERO
   ──────────────────────────────────────────────────────────────────────── */
/* ── TmsIrisLabels — 4 compass labels around the iris ring with live drift.
   Metrics chosen to match what TwinMs actually monitors (per the
   roadmap PDF). Real metrics only:
     N — Uptime (platform availability)
     E — Energy (savings vs 730-day baseline)
     S — IAQ (composite air-quality score 0-100)
     W — Water (savings vs 730-day flow baseline) */
function TmsIrisLabels() {
  const [v, setV] = useTmsState({ uptime: 99.7, energy: 12.4, iaq: 87, water: 7.4 });
  useTmsEffect(() => {
    const drift = (val, min, max, amp) =>
      Math.max(min, Math.min(max, val + (Math.random() - 0.5) * amp));
    const id = setInterval(() => {
      setV(prev => ({
        uptime: drift(prev.uptime, 99.4, 99.95, 0.04),
        energy: drift(prev.energy, 9, 16, 0.30),
        iaq:    drift(prev.iaq,    82, 93, 0.35),
        water:  drift(prev.water,  4,  11, 0.22)
      }));
    }, 250);
    return () => clearInterval(id);
  }, []);
  return (
    <>
      <div className="dz-tms-iris__label dz-tms-iris__label--n">
        Uptime
        <span className="dz-tms-iris__label-num">{v.uptime.toFixed(2)}%</span>
      </div>
      <div className="dz-tms-iris__label dz-tms-iris__label--e">
        Energy
        <span className="dz-tms-iris__label-num">−{v.energy.toFixed(1)}%</span>
      </div>
      <div className="dz-tms-iris__label dz-tms-iris__label--s">
        IAQ
        <span className="dz-tms-iris__label-num">{Math.round(v.iaq)}/100</span>
      </div>
      <div className="dz-tms-iris__label dz-tms-iris__label--w">
        Water
        <span className="dz-tms-iris__label-num">−{v.water.toFixed(1)}%</span>
      </div>
    </>
  );
}

function TmsHero({ data }) {
  const stripRef = useTmsReveal(0.3);

  return (
    <section className="dz-tms-hero">
      <div className="dz-tms-hero__inner">
        <div className="dz-tms-hero__col dz-tms-hero__col--copy">
          <img
            src={data.logoSrc}
            alt="TwinMs"
            className="dz-tms-hero__brand"
            loading="eager"
            decoding="async"
          />
          <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
          <h1 className="dz-tms-hero__title">
            {data.title.lead}{' '}
            <em className="dz-tms-accent">{data.title.accent}</em>
          </h1>
          <p className="dz-tms-hero__sub" dangerouslySetInnerHTML={{ __html: data.sub }} />
          {(data.cta || data.siteCta) && (
            <div style={{ margin: '-16px 0 36px', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {data.cta && <a className="dz-btn dz-btn--aqua" href={data.cta.href}>{data.cta.label}</a>}
              {data.siteCta && <a className="dz-btn dz-tms-sitebtn" href={data.siteCta.href} target="_blank" rel="noopener">
                <svg className="dz-tms-sitebtn__signal" width="18" height="14" viewBox="0 0 24 18" aria-hidden="true">
                  <path d="M2 7a14 14 0 0 1 20 0M5.5 10.5a9 9 0 0 1 13 0M9 14a4 4 0 0 1 6 0" stroke="currentColor" strokeWidth="2.4" fill="none" strokeLinecap="round" />
                </svg>
                {data.siteCta.label}
                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" style={{ transform: dzIsAr() ? 'scaleX(-1)' : 'none' }}>
                  <path d="M5 11l6-6M6 5h5v5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>}
            </div>
          )}

          <div className="dz-tms-hero__stats" ref={stripRef}>
            {data.stats.map((s, i) => (
              <div className="dz-tms-stat" key={i}>
                <div className="dz-tms-stat__num">
                  <TmsCountUp
                    to={s.num}
                    prefix={s.prefix || ''}
                    suffix={s.suffix || ''}
                  />
                </div>
                <div className="dz-tms-stat__label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="dz-tms-hero__col dz-tms-hero__col--visual">
          {/* Click-to-play video tour. Audio is enabled, so we don't autoplay
              (browsers block autoplay-with-sound). Visitor taps Play → video
              starts with native controls. The iris/scan visual moved to the
              "Smart alerts · 730-day baselines" section. */}
          <TmsHeroVideo
            src={dzMobileVideo(data.heroVideo)}
            poster={data.heroPoster}
            label={data.heroVideoLabel || 'TwinMs platform tour'}
          />
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   THE 3D TWIN
   ──────────────────────────────────────────────────────────────────────── */
function TmsThreeD({ data }) {
  const ref = useTmsReveal(0.2);
  // Lazy-load the 3D-twin video on intersection. Same pattern as
  // TmsUseCaseTile — preload="none" + IntersectionObserver. Saves
  // ~3 MB on mobile if the visitor never scrolls past the hero, which
  // is the common case for /twinms/ on phones.
  const sectionRef = useTmsRef(null);
  const videoRef   = useTmsRef(null);
  useTmsEffect(() => {
    const sectionEl = sectionRef.current;
    const videoEl   = videoRef.current;
    if (!sectionEl || !videoEl || !data.video) return;
    const src = dzMobileVideo(data.video);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          if (!videoEl.src) videoEl.src = src;
          const p = videoEl.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!videoEl.paused) {
          videoEl.pause();
        }
      });
    }, { threshold: 0.15, rootMargin: '200px' });
    io.observe(sectionEl);
    return () => io.disconnect();
  }, [data.video]);

  return (
    <section className="dz-tms-3d" ref={(el) => { ref.current = el; sectionRef.current = el; }}>
      <div className="dz-tms-3d__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tms-h2">{data.title}</h2>
        <p className="dz-tms-lead">{data.lead}</p>
      </div>

      <figure className="dz-tms-3d__frame">
        <div className="dz-tms-3d__device">
          <div className="dz-tms-3d__device-bar">
            <span /><span /><span />
          </div>
          {data.video ? (
            <video
              ref={videoRef}
              poster={data.image}
              muted
              loop
              playsInline
              preload="none"
              disablePictureInPicture
              disableRemotePlayback
              controlsList="nodownload nofullscreen noremoteplayback"
              aria-label={data.alt}
            />
          ) : (
            <img
              src={data.image}
              alt={data.alt}
              loading="lazy"
              decoding="async"
            />
          )}
        </div>
      </figure>

      <ul className="dz-tms-3d__captions">
        {data.captions.map((c, i) => (
          <li key={i}>
            <span className="dz-tms-3d__caption-kicker">{c.kicker}</span>
            <span className="dz-tms-3d__caption-text">{c.text}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   WHAT IT MONITORS
   ──────────────────────────────────────────────────────────────────────── */
function TmsMonitors({ data }) {
  const ref = useTmsReveal(0.15);
  return (
    <section className="dz-tms-mon" ref={ref}>
      <div className="dz-tms-mon__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tms-h2">{data.title}</h2>
        <p className="dz-tms-lead" dangerouslySetInnerHTML={{ __html: data.lead }} />
      </div>
      <div className="dz-tms-mon__grid">
        {data.tiles.map((t, i) => (
          <article className="dz-tms-tile" key={i} style={{ '--tms-tile-i': i }}>
            <div className="dz-tms-tile__icon"><TmsIcon name={t.icon} /></div>
            <div className="dz-tms-tile__label">{t.label}</div>
            <p className="dz-tms-tile__text">{t.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   USE CASES — One platform, many realities
   Each tile lazy-loads its video on intersection (keeps bandwidth sane).
   ──────────────────────────────────────────────────────────────────────── */
function TmsUseCaseTile({ tile, i, ctaLabel }) {
  const tileRef  = useTmsRef(null);
  const videoRef = useTmsRef(null);

  useTmsEffect(() => {
    const tileEl  = tileRef.current;
    const videoEl = videoRef.current;
    if (!tileEl || !videoEl) return;

    const src = dzMobileVideo(tile.preview || tile.video);
    if (!src) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          if (!videoEl.src) videoEl.src = src;
          const p = videoEl.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!videoEl.paused) {
          videoEl.pause();
        }
      });
    }, { threshold: 0.25 });

    io.observe(tileEl);
    return () => io.disconnect();
  }, [tile.preview, tile.video]);

  const pillMod = tile.status === 'Live' ? 'live' : 'demo';

  return (
    <a className="dz-tms-uc" href={tile.href} ref={tileRef} style={{ '--tms-uc-i': i }}>
      <div className="dz-tms-uc__media">
        <video
          ref={videoRef}
          className="dz-tms-uc__video"
          poster={tile.poster || undefined}
          muted
          loop
          playsInline
          preload="none"
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload nofullscreen noremoteplayback"
        />
        <span className={`dz-tms-uc__pill dz-tms-uc__pill--${pillMod}`}>
          <span className="dz-tms-uc__pill-dot" />
          {dzT(tile.status, tile.status === 'Live' ? 'مباشر' : 'عرض توضيحي')}{tile.statusNote ? ` · ${tile.statusNote}` : ''}
        </span>
      </div>
      <div className="dz-tms-uc__body">
        <div className="dz-tms-uc__label">{tile.label}</div>
        <h3 className="dz-tms-uc__title">{tile.title}</h3>
        <p className="dz-tms-uc__text">{tile.text}</p>
        <span className="dz-tms-uc__cta" aria-hidden="true">
          {ctaLabel || 'Watch the scenario'}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
    </a>
  );
}

function TmsUseCases({ data }) {
  const ref      = useTmsReveal(0.12);
  const reelRef  = useTmsRef(null);
  const [active, setActive] = useTmsState(0);
  const total = data.tiles.length;

  /* Track which tile is centered in the reel — updates active dot + counter */
  useTmsEffect(() => {
    const reel = reelRef.current;
    if (!reel) return;
    const panels = reel.querySelectorAll('.dz-tms-use__slot');
    if (!panels.length) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.55) {
          const i = parseInt(e.target.dataset.i, 10);
          if (!Number.isNaN(i)) setActive(i);
        }
      });
    }, { root: reel, threshold: [0, 0.55, 1] });
    panels.forEach((p) => io.observe(p));
    return () => io.disconnect();
  }, []);

  /* Keyboard nav — only when focus is inside the reel section */
  useTmsEffect(() => {
    const section = ref.current;
    if (!section) return;
    const onKey = (e) => {
      if (!section.contains(document.activeElement)) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(active + 1); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); goTo(active - 1); }
    };
    section.addEventListener('keydown', onKey);
    return () => section.removeEventListener('keydown', onKey);
  });

  function goTo(i) {
    const reel = reelRef.current;
    if (!reel) return;
    const target = Math.max(0, Math.min(total - 1, i));
    const panel = reel.querySelectorAll('.dz-tms-use__slot')[target];
    if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  function pad(n) { return String(n).padStart(2, '0'); }

  return (
    <section className="dz-tms-use" id="use-cases" ref={ref}>
      <div className="dz-tms-use__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tms-h2">{data.title}</h2>
        <p className="dz-tms-lead" dangerouslySetInnerHTML={{ __html: data.lead }} />
      </div>

      <div className="dz-tms-use__reel" ref={reelRef} role="region" aria-label="Use cases" tabIndex="0">
        {data.tiles.map((t, i) => (
          <div className="dz-tms-use__slot" data-i={i} key={i}>
            <TmsUseCaseTile tile={t} i={i} ctaLabel={data.cta} />
          </div>
        ))}
      </div>

      <div className="dz-tms-use__controls">
        <div className="dz-tms-use__counter" aria-live="polite">
          <strong>{pad(active + 1)}</strong>
          <span className="dz-tms-use__counter-sep"> · </span>
          <span className="dz-tms-use__counter-total">{pad(total)}</span>
        </div>

        <div className="dz-tms-use__dots" role="tablist" aria-label="Use cases">
          {data.tiles.map((_, i) => (
            <button key={i}
                    role="tab"
                    aria-selected={i === active}
                    aria-label={'Go to use case ' + (i + 1)}
                    className={'dz-tms-use__dot' + (i === active ? ' is-active' : '')}
                    onClick={() => goTo(i)} />
          ))}
        </div>

        <div className="dz-tms-use__arrows">
          <button className="dz-tms-use__arrow"
                  aria-label="Previous use case"
                  disabled={active === 0}
                  onClick={() => goTo(active - 1)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" aria-hidden="true">
              <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button className="dz-tms-use__arrow"
                  aria-label="Next use case"
                  disabled={active === total - 1}
                  onClick={() => goTo(active + 1)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   ALERTS + 730-DAY BASELINES
   ──────────────────────────────────────────────────────────────────────── */
/* TmsIris — reusable iris (circular building scan with rings, sweep, labels).
   Moved out of TmsHero so it can also be embedded in the Smart alerts section.
   Self-contained: owns the inner video element + its loop+slowdown choreography. */
function TmsIris({ videoSrc, posterSrc, videoLoopStart }) {
  const videoRef = useTmsRef(null);
  const containerRef = useTmsRef(null);

  useTmsEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.loop = false;
    v.playbackRate = 1.0;
    const loopStart = videoLoopStart || 3.0;
    const slowAfter = 2.0;
    const slowRate  = 0.3;

    const onTime = () => {
      const t = v.currentTime;
      if (t >= slowAfter && v.playbackRate !== slowRate) v.playbackRate = slowRate;
      else if (t < slowAfter && v.playbackRate !== 1.0) v.playbackRate = 1.0;
    };
    const onEnded = () => {
      try {
        v.playbackRate = slowRate;
        v.currentTime  = loopStart;
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } catch (_) {}
    };

    v.addEventListener('timeupdate', onTime);
    v.addEventListener('ended',      onEnded);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('ended',      onEnded);
    };
  }, [videoSrc, videoLoopStart]);

  // Lazy-load: TmsIris lives inside the Alerts section (well below the
  // fold on every viewport). Don't fetch the video bytes until the iris
  // scrolls into view. Once it does, set src + play; pause when leaving.
  useTmsEffect(() => {
    const cont = containerRef.current;
    const v    = videoRef.current;
    if (!cont || !v || !videoSrc) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          if (!v.src) v.src = videoSrc;
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { threshold: 0.2, rootMargin: '200px' });
    io.observe(cont);
    return () => io.disconnect();
  }, [videoSrc]);

  return (
    <div className="dz-tms-iris" ref={containerRef}>
      <div className="dz-tms-iris__ring--outer" aria-hidden="true" />
      <div className="dz-tms-iris__ticks"       aria-hidden="true" />
      <div className="dz-tms-iris__ring--mid"   aria-hidden="true" />
      <div className="dz-tms-iris__ring--inner" aria-hidden="true" />
      <div className="dz-tms-iris__video">
        <video
          ref={videoRef}
          poster={posterSrc}
          muted
          playsInline
          preload="none"
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload nofullscreen noremoteplayback"
          aria-label="TwinMs digital twin — operational overview"
        />
      </div>
      <div className="dz-tms-iris__sweep" aria-hidden="true" />
      <div className="dz-tms-iris__dot dz-tms-iris__dot--n" aria-hidden="true" />
      <div className="dz-tms-iris__dot dz-tms-iris__dot--e" aria-hidden="true" />
      <div className="dz-tms-iris__dot dz-tms-iris__dot--s" aria-hidden="true" />
      <div className="dz-tms-iris__dot dz-tms-iris__dot--w" aria-hidden="true" />
      <div className="dz-tms-iris__crosshair" aria-hidden="true">
        <span className="dz-tms-iris__crosshair-dot" />
      </div>
      <TmsIrisLabels />
    </div>
  );
}

/* TmsHeroVideo — click-to-play video with a custom play button overlay.
   Audio is enabled (no `muted`) which is why we DON'T autoplay (browsers
   block autoplay-with-sound). Visitor taps Play → video starts with
   native controls visible. */
function TmsHeroVideo({ src, poster, label }) {
  const videoRef = useTmsRef(null);
  const [hasStarted, setHasStarted] = useTmsState(false);

  const handlePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    setHasStarted(true);
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  return (
    <div className="dz-macbook">
      <div className="dz-macbook-screen">
        <span className="dz-macbook-notch" aria-hidden="true" />
        <div className={`dz-macbook-bezel dz-tms-hero__video-wrap${hasStarted ? ' is-playing' : ''}`}>
          <video
            ref={videoRef}
            src={src}
            poster={poster}
            playsInline
            preload="metadata"
            controls={hasStarted}
            disablePictureInPicture
            disableRemotePlayback
            controlsList="nodownload nofullscreen noremoteplayback"
            aria-label={label || 'TwinMs intro video'}
          />
          {!hasStarted && (
            <button
              type="button"
              className="dz-tms-hero__play"
              onClick={handlePlay}
              aria-label="Play TwinMs intro video"
            >
              <span className="dz-tms-hero__play-circle" aria-hidden="true">
                <svg viewBox="0 0 80 80" width="60" height="60" fill="none">
                  <circle cx="40" cy="40" r="38" fill="#4FC8F4" />
                  <polygon points="32,26 32,54 56,40" fill="#0F1E2C" />
                </svg>
              </span>
              <span className="dz-tms-hero__play-label">{dzT('Watch the platform tour', 'شاهد جولة المنصّة')}</span>
            </button>
          )}
        </div>
      </div>
      <span className="dz-macbook-base" aria-hidden="true" />
    </div>
  );
}

function TmsAlerts({ data }) {
  const ref = useTmsReveal(0.2);
  return (
    <section className="dz-tms-alerts" ref={ref}>
      <div className="dz-tms-alerts__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tms-h2">{data.title}</h2>
      </div>

      {/* The iris (radar-scan visual w/ 4 directional metrics) — moved here
          from the hero. The "scan" + "730-day baselines" framing matches
          this section's narrative. */}
      {data.iris && (
        <div className="dz-tms-alerts__visual">
          <TmsIris
            videoSrc={dzMobileVideo(data.iris.videoSrc)}
            posterSrc={data.iris.posterSrc}
            videoLoopStart={data.iris.videoLoopStart}
          />
        </div>
      )}

      <div className="dz-tms-alerts__grid">
        {data.columns.map((col, i) => (
          <article className="dz-tms-acard" key={i}>
            <div className="dz-tms-acard__kicker">{col.kicker}</div>
            <h3 className="dz-tms-acard__title">{col.title}</h3>
            <p className="dz-tms-acard__lead">{col.lead}</p>
            <ul className="dz-tms-acard__points">
              {col.points.map((p, j) => <li key={j}>{p}</li>)}
            </ul>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   LIVE PILOT — DAR ALBADRIYAH
   ──────────────────────────────────────────────────────────────────────── */
function TmsPilot({ data }) {
  const ref = useTmsReveal(0.2);
  return (
    <section className="dz-tms-pilot" ref={ref}>
      <div className="dz-tms-pilot__inner">
        <div className="dz-tms-pilot__head">
          <span className="dz-eyebrow dz-tms-eyebrow dz-tms-eyebrow--on-dark">{data.eyebrow}</span>
          <h2 className="dz-tms-h2 dz-tms-h2--on-dark">{data.title}</h2>
          <p className="dz-tms-lead dz-tms-lead--on-dark">{data.lead}</p>
        </div>

        <div className="dz-tms-pilot__cols dz-tms-pilot__cols--single">
          <div className="dz-tms-pilot__col">
            <div className="dz-tms-pilot__col-title">{dzT('Live in production', 'مباشر في الإنتاج')}</div>
            <ul className="dz-tms-pilot__list">
              {data.status.map((s, i) => (
                <li key={i}>
                  <span className="dz-tms-pilot__dot dz-tms-pilot__dot--good" aria-hidden="true" />
                  <span className="dz-tms-pilot__label">{s.label}</span>
                  <span className="dz-tms-pilot__state">{s.state}</span>
                </li>
              ))}
            </ul>
          </div>
          {/* Single-column layout: every layer is live, no "in progress"
              parallel column needed and no honesty-disclaimer paragraph. */}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   BUILT ON THE BIM MODEL
   ──────────────────────────────────────────────────────────────────────── */
function TmsBim({ data }) {
  const ref = useTmsReveal(0.25);
  return (
    <section className="dz-tms-bim" ref={ref}>
      <div className="dz-tms-bim__inner">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tms-h2">{data.title}</h2>
        <p className="dz-tms-lead">{data.lead}</p>
        <a className="dz-tms-bim__cta" href={data.cta.href}>
          {data.cta.label}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </a>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   WHAT'S SHIPPING NEXT
   ──────────────────────────────────────────────────────────────────────── */
function TmsNext({ data }) {
  const ref = useTmsReveal(0.15);
  return (
    <section className="dz-tms-next" ref={ref}>
      <div className="dz-tms-next__head">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tms-h2">{data.title}</h2>
        <p className="dz-tms-lead">{data.lead}</p>
      </div>
      <div className="dz-tms-next__grid">
        {data.items.map((it, i) => (
          <article className="dz-tms-ncard" key={i} style={{ '--tms-next-i': i }}>
            <div className="dz-tms-ncard__kicker">{it.kicker} · {dzT('In production', 'في الإنتاج')}</div>
            <h3 className="dz-tms-ncard__label">{it.label}</h3>
            <p className="dz-tms-ncard__text">{it.text}</p>
          </article>
        ))}
      </div>
      <div className="dz-tms-next__cta">
        <p className="dz-tms-next__cta-line">{dzT('Ready to see TwinMs in your building?', 'جاهز لرؤية TwinMs في منشأتك؟')}</p>
        <a className="dz-tms-next__cta-btn" href={dzT('/contact/?topic=twinms', '/ar/contact/?topic=twinms')}>
          {dzT('Request a demo', 'اطلب عرضاً توضيحياً')}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   DEPTH PROSE BANDS + FAQ (OPP-0009) — additive sections below the product
   experience. Reuse existing anatomy only: the TmsBim section classes and
   the global home.css FAQ accordion. Each renders only when the content
   layer provides its data (EN + AR parity via content/twinms*.js).
   ──────────────────────────────────────────────────────────────────────── */
function TmsProse({ data }) {
  const ref = useTmsReveal(0.2);
  if (!data) return null;
  return (
    <section className="dz-tms-bim" ref={ref}>
      <div className="dz-tms-bim__inner">
        <span className="dz-eyebrow dz-tms-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-tms-h2">{data.title}</h2>
        {(data.paragraphs || []).map((p, i) => (
          <p className="dz-tms-lead" key={i} style={{ marginTop: i === 0 ? 0 : 14 }}
             dangerouslySetInnerHTML={{ __html: p }} />
        ))}
        {data.steps && (
          <ol className="dz-tms-lead" style={{ margin: '14px 0 0', paddingInlineStart: '1.3em' }}>
            {data.steps.map((s, i) => (
              <li key={i} style={{ marginTop: i === 0 ? 0 : 10 }}
                  dangerouslySetInnerHTML={{ __html: s }} />
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

/* FAQ — mirrors the homepage accordion markup (all classes styled in the
   shared home.css). No reveal ref: renders statically to avoid any
   visibility coupling with the homepage's dz-reveal animation classes. */
function TmsFaq({ data }) {
  if (!data) return null;
  return (
    <section className="dz-section dz-faq" aria-label={data.sectionAriaLabel}>
      <div className="dz-section__inner">
        <span className="dz-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-h2">{data.titleLeading} <em>{data.titleAccent}</em></h2>
        <p className="dz-lead">{data.lead}</p>
        <div className="dz-faq__list" id="dz-tms-faq-list">
          {data.items.map((it, i) => (
            <details key={i} className="dz-faq-item">
              <summary>{it.q}</summary>
              {it.aHtml
                ? <div className="dz-faq-item__body" dangerouslySetInnerHTML={{ __html: it.aHtml }} />
                : <div className="dz-faq-item__body">{it.a}</div>}
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   PAGE ROOT
   ──────────────────────────────────────────────────────────────────────── */
// Swap auto-loading product videos to their -mobile.mp4 variants on
// viewports ≤800px. Same pattern as Hero.jsx — saves 70-98% on each
// video. Use only for decorative autoplay-loop videos; keep desktop
// quality for videos with controls (users PLAY them).
function dzMobileVideo(url) {
  if (typeof window === 'undefined' || !url) return url;
  if (!window.matchMedia || !window.matchMedia('(max-width: 800px)').matches) return url;
  return url.replace(/(\.mp4)(\?[^"'\s]*)?$/i, '-mobile$1$2');
}

/* PROOF — related Design Zone projects as cards (same design as the home "Our work" cards). */
function TmsProof({ data }) {
  if (!data || !data.items) return null;
  const arrow = (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" className="dz-featured__arrow">
      <path d="M3 8h9M8 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  return (
    <section className="dz-featured" aria-label={data.eyebrow}>
      <div className="dz-featured__inner">
        <div className="dz-featured__head">
          <div className="dz-featured__head-left">
            <span className="dz-eyebrow">{data.eyebrow}</span>
            <h2 className="dz-featured__title">{data.titleLeading} <em>{data.titleAccent}</em></h2>
          </div>
          <p className="dz-featured__lead">{data.lead}</p>
        </div>
        <div className="dz-featured__grid">
          {data.items.map((p) => (
            <a key={p.slug} className="dz-featured__card" href={p.href}>
              <div className="dz-featured__media">
                <picture>
                  {p.img.small && <source type="image/avif" srcSet={p.img.large ? p.img.small + ' 800w, ' + p.img.large + ' 1600w' : p.img.small} sizes="(max-width: 600px) 92vw, (max-width: 960px) 46vw, 420px" />}
                  <img src={p.img.fallback} alt={p.alt} loading="lazy" decoding="async" width="800" height="500" />
                </picture>
              </div>
              <div className="dz-featured__body">
                <span className="dz-featured__tag">{p.tag}</span>
                <h3 className="dz-featured__name">{p.name}</h3>
                <p className="dz-featured__desc">{p.desc}</p>
                <span className="dz-featured__link">{data.linkLabel} {arrow}</span>
              </div>
            </a>
          ))}
        </div>
        <div className="dz-featured__foot">
          <a className="dz-featured__all" href={data.allHref}>{data.allLabel} {arrow}</a>
        </div>
      </div>
    </section>
  );
}

function TwinMsPage() {
  const data = window.DZ_TWINMS;

  if (!data) {
    /* Defensive: content layer didn't load — render a minimal shell rather
       than throwing so the page header/footer are still visible. */
    return (
      <main className="dz-tms-page dz-tms-page--missing">
        <section className="dz-tms-hero">
          <div className="dz-tms-hero__inner">
            <h1 className="dz-tms-hero__title">TwinMs</h1>
            <p className="dz-tms-hero__sub">Content layer failed to load. See console.</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="dz-tms-page" id="dz-main">
      {data.hero     && <TmsHero      data={data.hero} />}
      {data.threeD   && <TmsThreeD    data={data.threeD} />}
      {data.monitors && <TmsMonitors  data={data.monitors} />}
      {data.useCases && <TmsUseCases  data={data.useCases} />}
      {data.proof    && <TmsProof     data={data.proof} />}
      {data.alerts   && <TmsAlerts    data={data.alerts} />}
      {data.bim      && <TmsBim       data={data.bim} />}
      {data.whatIsTwin && <TmsProse   data={data.whatIsTwin} />}
      {data.bimToTwin  && <TmsProse   data={data.bimToTwin} />}
      {data.saudi      && <TmsProse   data={data.saudi} />}
      {data.faq        && <TmsFaq     data={data.faq} />}
      {data.next     && <TmsNext      data={data.next} />}
    </main>
  );
}

window.TwinMsPage = TwinMsPage;
